import { IListItem } from '../../../../interfaces/listItem';
import { IExperience } from '../../../../interfaces/experience';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useContext } from 'react';
import { ColumnBoxFlexFullWidth, FlexBox, FlexBoxFullWidth } from '../../../common/boxes';
import { Typography } from '@mui/material';
import { MONTH_NAMES } from '../common/ExperienceOrEducationEntityModal/Dates';
import { TEXTS } from '../../../../core/texts';
import ProfileListItem from '../common/ProfileListItem';
import { TrendingFlat } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FreelancerContext } from '../FreelancerEntity';
import { FreelancerService } from '../../../../services/freelancer';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../../interfaces/errorResponse';
import { FREELANCER_ENTITY_QUERY_KEY } from '../../../../queryHooks/freelancer/useGetFreelancerEntity';
import { useNotification } from '../../../../+state/useNotification';

export const EXPERIENCE_QUERY_PARAM = 'experience';

const ExperienceListItem = ({ entity }: IListItem<IExperience>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(FreelancerContext);
  const { openSuccessToast, openErrorToast } = useNotification();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => FreelancerService.deleteExperience(user.id, entity.id!),
    onSuccess: () => {
      openSuccessToast(TEXTS.SUCCESS_DELETED);
      queryClient.invalidateQueries([FREELANCER_ENTITY_QUERY_KEY]);
    },
    onError: (err: AxiosError<IErrorResponse>) => {
      openErrorToast(err.response?.data.message || err.message);
    }
  });

  const handleClickEdit = useCallback(() => {
    searchParams.set(EXPERIENCE_QUERY_PARAM, entity.id!.toString());
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams, entity.id]);

  return (
    <ProfileListItem deleteHandler={deleteMutation.mutate} editHandler={handleClickEdit}>
      <ColumnBoxFlexFullWidth alignItems={'flex-start'}>
        <Typography variant={'h6'}>
          {entity.company}
        </Typography>

        <Typography variant={'h4'}>
          {entity.role}
        </Typography>

        <FlexBoxFullWidth alignItems={'center'} mt={1}>
          <FlexBox>
            <Typography>
              {entity.startDateMonth && entity.startDateYear && MONTH_NAMES[entity.startDateMonth - 1].slice(0, 3) + "-"}
            </Typography>
            <Typography>
              {entity.startDateYear ?? "N/A"}
            </Typography>
          </FlexBox>
          <TrendingFlat sx={{ mx: 1 }}/>
          {
            (entity.endDateMonth && entity.endDateYear)
              ? <FlexBox>
                <Typography>
                  {`${MONTH_NAMES[entity.endDateMonth - 1].slice(0, 3)}-${entity.endDateYear}`}
                </Typography>
              </FlexBox>
              : <Typography>
                {TEXTS.UNTIL_NOW}
              </Typography>
          }
        </FlexBoxFullWidth>
        
        <Typography variant={'body2'} sx={{ my: 2 }}>
          {entity.description}
        </Typography>
      </ColumnBoxFlexFullWidth>
    </ProfileListItem>
  );
};


export default ExperienceListItem;